import React, { useState } from 'react';
import Button from '@atlaskit/button';
import { useIntl } from '@atlassian/jira-intl';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import ObjectIssueCard from '@atlassian/jira-servicedesk-insight-object-issue-card/src/ui/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const VISIBLE_INCIDENTS_MAX_COUNT = 2;
const IncidentIssueCards = ({ incidents }: Props) => (
	<>
		{incidents.map((incident) => (
			<ObjectIssueCard
				key={incident.issueKey}
				issueKey={incident.issueKey}
				issueSummary={incident.issueSummary}
				issueTypeIconUrl={incident.issueTypeIconUrl}
			/>
		))}
	</>
);

const IncidentList = ({ incidents }: Props) => {
	const { formatMessage } = useIntl();
	const visibleIncidents = incidents.slice(0, VISIBLE_INCIDENTS_MAX_COUNT);
	const hiddenIncidents = incidents.slice(VISIBLE_INCIDENTS_MAX_COUNT);
	const [isListExpanded, setIsListExpanded] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const route = useCurrentRoute();

	return (
		<>
			<IncidentIssueCards incidents={visibleIncidents} />
			{hiddenIncidents.length > 0 &&
				(isListExpanded ? (
					<>
						<IncidentIssueCards incidents={hiddenIncidents} />
						<Button
							appearance="link"
							spacing="none"
							onClick={() => {
								fireUIAnalytics(
									createAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
									}),
									'showLessIncidentButton',
									{
										routeGroup: route.group ?? undefined,
										routeName: route.name ?? undefined,
									},
								);
								setIsListExpanded(!isListExpanded);
							}}
						>
							{formatMessage(messages.lessIncidents)}
						</Button>
					</>
				) : (
					<Button
						appearance="link"
						spacing="none"
						onClick={() => {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
								}),
								'showMoreIncidentsButton',
								{
									routeGroup: route.group ?? undefined,
									routeName: route.name ?? undefined,
								},
							);
							setIsListExpanded(!isListExpanded);
						}}
					>
						{formatMessage(messages.moreIncidents, {
							incidentCount: hiddenIncidents.length,
						})}
					</Button>
				))}
		</>
	);
};

export default IncidentList;
